import { useState } from 'react'
import { Legend } from './legend'
import type { Series } from './legend'
import { formatNumber, niceMax, shortDay, ticks } from './scale'
import { useWidth } from './use-width'
import styles from './charts.module.css'

type Props = {
  readonly label: string
  readonly days: readonly string[]
  readonly series: readonly Series[]
  readonly value: (day: string, key: string) => number
  readonly height?: number
}

const PAD = { top: 8, right: 8, bottom: 24, left: 36 }

export function StackedBars({ label, days, series, value, height = 220 }: Props) {
  const { ref, width } = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const totals = days.map((d) => series.reduce((sum, s) => sum + value(d, s.key), 0))
  const max = niceMax(Math.max(0, ...totals))
  const plotW = width - PAD.left - PAD.right
  const plotH = height - PAD.top - PAD.bottom
  const slot = plotW / Math.max(1, days.length)
  const barW = Math.max(2, Math.min(28, slot - 2))
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH
  const labelEvery = Math.ceil(days.length / Math.max(1, Math.floor(plotW / 48)))
  const hovered = hover === null ? undefined : days[hover]

  return (
    <div ref={ref} className={styles.chart}>
      {series.length > 1 && <Legend series={series} />}
      <svg width={width} height={height} role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {ticks(max).map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} className={styles.grid} />
            <text x={PAD.left - 6} y={y(t)} className={styles.axis} textAnchor="end" dominantBaseline="middle">
              {formatNumber(t)}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = PAD.left + i * slot + (slot - barW) / 2
          let acc = 0
          return (
            <g key={d} opacity={hover === null || hover === i ? 1 : 0.55}>
              {series.map((s) => {
                const v = value(d, s.key)
                if (v <= 0) return null
                const top = y(acc + v)
                const h = y(acc) - top
                acc += v
                return <rect key={s.key} x={x} y={top + 1} width={barW} height={Math.max(1, h - 2)} rx={2} fill={s.color} />
              })}
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={height - 6} className={styles.axis} textAnchor="middle">
                  {shortDay(d)}
                </text>
              )}
              <rect
                x={PAD.left + i * slot}
                y={PAD.top}
                width={slot}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
            </g>
          )
        })}
      </svg>
      {hovered !== undefined && hover !== null && (
        <div
          className={styles.tooltip}
          style={{ left: Math.min(width - 180, Math.max(0, PAD.left + hover * slot + slot / 2 - 90)) }}
          role="status"
        >
          <div className={styles.tooltipTitle}>
            {shortDay(hovered)} · всего {formatNumber(totals[hover] ?? 0)}
          </div>
          {series.map((s) => (
            <div key={s.key} className={styles.tooltipRow}>
              <span className={styles.swatch} style={{ background: s.color }} aria-hidden="true" />
              <span>{s.label}</span>
              <span className={styles.tooltipValue}>{formatNumber(value(hovered, s.key))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
